import { jellyBeans } from './constants'

export function buildJellyBeanBackground(id) {
  let key = id
  if (id === 9999) {
    key = Math.floor(Math.random() * 25) + 1
  }

  const { start = '#638FFF', stop = '#3B5DB2' } = jellyBeans.find(
    b => b.id === key
  )
  const beanBackground = {
    background: `linear-gradient(${start} 0%, ${stop} 100%) 
    0% 0% no-repeat padding-box`
  }

  return beanBackground
}

export const randomJellyBean = () =>
  Math.floor(Math.random() * jellyBeans.length) + 1

// For the "Me" menu, profile panel, resolves family/given name (which may not both exist):

export function getDisplayNameFromToken(user) {
  if (!user) return {}
  const { email = '', family_name = '', given_name = '' } = user

  // If given_name or family_name are arrays, get the value from position 1.
  const familyName =
    Array.isArray(family_name) && family_name[1] ? family_name[1] : family_name

  const givenName =
    Array.isArray(given_name) && given_name[1] ? given_name[1] : given_name

  const displayName = givenName || email
  const fullName =
    givenName || familyName ? `${givenName || ''} ${familyName || ''}` : email

  return { displayName, fullName: fullName.trim() }
}

// For the avatars and to resolve a full name for a contact, etc:
export function getDisplayNameAndInitials(person) {
  const { name, email, phone } = person || {}
  const displayName = name || email || phone || 'Unknown'
  const excludePhone = name || email || '-'
  const initials = excludePhone
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  return { displayName, initials }
}

// Adds commas to numbers. E.g. 1234567 becomes 1,234,567
const numberWithCommas = (x, priceConversion) => {
  if (x === undefined || x === null) {
    return 0
  }
  let val = x
  if (priceConversion) val = Number(x).toFixed(2)
  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const titleCase = str => {
  let splitStr = []
  if (!str) return undefined
  if (typeof str !== 'string') return str
  splitStr = str.toLowerCase().split(' ')
  for (let i = 0; i < splitStr.length; i += 1) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
  }
  // Directly return the joined string
  return splitStr.join(' ')
}

const capitalize = s => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const getDecimalCount = scaled => {
  if (scaled >= 99.5) {
    return 0
  }
  if (scaled >= 9.995) {
    return 1
  }
  if (scaled >= 0.9995) {
    return 2
  }
  return 0
}
const getTier = number => {
  // eslint-disable-next-line no-bitwise
  const tier = (Math.log10(number) / 3) | 0
  return tier
}
const getSuffix = tier =>
  // JA:  I understand that reaching a "B" (billion) or more may not be realistic in practice.
  //      However, since the user has the option to use a custom evaluator,
  //      it's important to safeguard against undefined values.
  //      Therefore, I'm incorporating additional measures to address this.
  ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'O', 'N', 'D'][tier]

// eslint-disable-next-line no-restricted-properties
const getScale = tier => Math.pow(10, tier * 3)

const abbreviateNumber = number => {
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(number) || number === null) {
    return number
  }
  const tier = getTier(number)
  const suffix = getSuffix(tier)
  const scale = getScale(tier)
  const scaled = number / scale
  const decimalCount = getDecimalCount(scaled)
  const rounded = scaled.toFixed(decimalCount)
  return rounded + suffix
}

function getPercentageChange(oldNumber, newNumber) {
  const decreaseValue = oldNumber - newNumber
  return (decreaseValue / oldNumber) * 100
}

function isEmptyObject(value) {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  )
}

function formatKey(val) {
  return val
    .replace(/([A-Z])/g, match => ` ${match}`)
    .replace(/^./, match => match.toUpperCase())
}

function serialize(obj) {
  return Object.keys(obj)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
    .join('&')
}

function getDomain() {
  const domainParts = window.location.hostname.split('.')
  if (domainParts.length >= 3) {
    return domainParts.slice(1, domainParts.length).join('.')
  }
  return domainParts.join('.')
}

function redirectToAgentUrl(entityId, agentId) {
  if (!agentId || agentId !== entityId) {
    const { protocol, port, pathname, hostname } = window.location
    let url = hostname.split('.')

    if (url.find(p => p === 'localhost')) {
      url = `${url[url.length - 1]}:${port}`
    } else {
      url = url.slice(url.length - 2).join('.')
    }

    const newUrl = `${protocol}//${entityId}.${url}${pathname}`
    window.location = newUrl
  }
}

function lotSizeLabel(sqFt) {
  if (sqFt <= 10000) {
    return `${numberWithCommas(sqFt)} Sq Ft`
  }
  let acres = sqFt / 43560
  acres = acres <= 1 ? `${acres} Acre` : `${acres} Acres`
  return acres
}

function areKeysAllNullOrEmpty(obj) {
  let keysAllNullOrEmpty = true
  Object.keys(obj).forEach(key => {
    if (obj[key] !== null) {
      keysAllNullOrEmpty = false
    }
  })
  return keysAllNullOrEmpty
}

function compactObj(obj) {
  const clone = { ...obj }
  Object.keys(clone).forEach(key => {
    const keyObj = clone[key]
    if (keyObj === '' || keyObj === null) {
      delete clone[key]
    } else if (Array.isArray(keyObj)) {
      if (keyObj.length === 0) {
        delete clone[key]
      }
    } else if (typeof keyObj === 'object') {
      if (areKeysAllNullOrEmpty(keyObj)) {
        delete clone[key]
      } else {
        clone[key] = compactObj(keyObj)
      }
    }
  })
  return clone
}

function textEllipsis(str, maxLength, { side = 'end', ellipsis = '...' } = {}) {
  if (!str) return ''
  if (str.length > maxLength) {
    switch (side) {
      case 'start':
        return ellipsis + str.slice(-(maxLength - ellipsis.length))
      case 'end':
      default:
        return `${str.slice(0, maxLength - ellipsis.length)} ${ellipsis}`
    }
  }
  return str
}

function hex(value) {
  return Math.floor(value).toString(16)
}
function pseudoObjectId() {
  return (
    hex(Date.now() / 1000) +
    ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16))
  )
}

const setNestedProp = (obj = {}, [first, ...rest], value) => ({
  ...obj,
  [first]: rest.length ? setNestedProp(obj[first], rest, value) : value
})

const uniqueKeyForParcelListing = p => {
  const { docId, upi } = p
  let key = upi || Math.random()
  if (docId) {
    if (docId.upi) {
      key = docId.upi
    } else if (docId.listingId) {
      key = `${docId.sourceMlsId}_${docId.listingId}`
    }
  }
  return key
}

const getSourceSet = sources => {
  const srcset = Object.keys(sources).map(s => {
    const width = s.split('x')[0]
    return `${sources[s]} ${width}w`
  })
  return srcset.join(', ')
}

// https://stackoverflow.com/a/72502261/731218
const isTouchScreen = () => {
  // TODO:
  // This function has some issues detecting touch screen.
  // I recommend to use "useDetectIfTouchScreen()" hook from ./customHooks
  // JA: 09/20/24

  let hasTouchScreen = false

  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = navigator.msMaxTouchPoints > 0
  } else {
    const mQ = window.matchMedia && matchMedia('(pointer:coarse)')
    if (mQ && mQ.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches
    } else if ('orientation' in window) {
      hasTouchScreen = true // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      const UA = navigator.userAgent
      hasTouchScreen =
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
    }
  }
  return hasTouchScreen
}

const stringToTestTag = string =>
  string
    .replace(/[^a-zA-Z ]/g, '')
    .toLowerCase()
    .split(' ')
    .join('-')

function deepMerge(defaults = {}, props) {
  // Base case: if prop is not an object or is null, return props or defaults
  if (typeof props !== 'object' || props === null) {
    return props !== undefined ? props : defaults
  }

  // Don't mutate the original default object, it's reference is typically shared:
  const merged = { ...defaults }

  for (const key in props) {
    if (
      props[key] &&
      typeof props[key] === 'object' &&
      !Array.isArray(props[key])
    ) {
      merged[key] = deepMerge(defaults[key], props[key])
    } else {
      merged[key] = props[key]
    }
  }

  return merged
}

function isValidURL(url) {
  const regex =
    /^(https?|ftp):\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(:[0-9]{1,5})?(\/.*)?$/
  return regex.test(url)
}

function validateURL(website) {
  let url = null
  if (website) {
    if (isValidURL(website)) {
      url = website
    } else {
      url = `https://${website}`
      if (!isValidURL(url)) {
        url = null
      }
    }
  }
  return url
}

export {
  deepMerge,
  stringToTestTag,
  numberWithCommas,
  titleCase,
  capitalize,
  abbreviateNumber,
  getPercentageChange,
  isEmptyObject,
  formatKey,
  serialize,
  getDomain,
  redirectToAgentUrl,
  lotSizeLabel,
  compactObj,
  textEllipsis,
  pseudoObjectId,
  setNestedProp,
  uniqueKeyForParcelListing,
  getSourceSet,
  isTouchScreen,
  isValidURL,
  validateURL
}
